import { useEffect, useState } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { RejectReasonDialog } from '../../components/common/RejectReasonDialog';
import { articleStatusLabel, displayTitle, roleLabel } from '../../utils/labels';
import { ShellCard } from '../../components/layout/ShellCard';
import { ErrorAlert } from '../../components/common/ErrorAlert';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { useFeedback } from '../../components/common/FeedbackProvider';
import { useAdminContent } from '../../hooks/useAdminContent';
import { useAuth } from '../../hooks/useAuth';
import { ARTICLE_FILTERS } from '../../utils/constants';
import { canAdminPublish, canCreate, canPublishApproved, canReviewArticle } from '../../utils/roles';
import { shellSx, tokens } from '../../theme/tokens';

export function ContentTab() {
  const { user } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const role = user.role;
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParam = searchParams.get('status') || 'all';
  const filter = ARTICLE_FILTERS.includes(filterParam) ? filterParam : 'all';
  const qParam = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(qParam);
  const debouncedQ = useDebouncedValue(searchInput, 350);
  const [confirm, setConfirm] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  useEffect(() => {
    setSearchInput(qParam);
  }, [qParam]);

  useEffect(() => {
    const next = debouncedQ.trim();
    if (next === qParam) return;
    const params = {};
    if (filter !== 'all') params.status = filter;
    if (next) params.q = next;
    setSearchParams(params, { replace: true });
  }, [debouncedQ, filter, qParam, setSearchParams]);
  const {
    items,
    loading,
    error,
    busy,
    setError,
    submit,
    approve,
    reject,
    publish,
    unpublish,
    remove,
  } = useAdminContent(filter, qParam);

  return (
    <Box>
      <ErrorAlert message={error} onClose={() => setError(null)} />

      {canCreate(role) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Button
            component={RouterLink}
            to="/admin/content/new"
            variant="contained"
            color="secondary"
            sx={{
              color: '#fff',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            New article
          </Button>
        </Stack>
      )}

      <Typography
        variant="body2"
        sx={{
          mb: 1.5,
          fontSize: { xs: '0.9375rem', sm: '1rem' },
          ...shellSx.textMuted,
        }}
      >
        {role === 'maker'
          ? 'Your drafts. Send one for review when it is ready.'
          : role === 'checker'
            ? 'Stories waiting for review, plus anything you wrote.'
            : 'Drafts, reviews, and live stories you can publish.'}
      </Typography>

      <ShellCard
        sx={{ overflow: 'hidden' }}
        contentSx={{ p: 0, '&:last-child': { pb: 0 } }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.25, sm: 1.5 },
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Stack spacing={1.25}>
            <TextField
              size="small"
              label="Search stories"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {(role === 'checker'
                ? ['pending', 'approved', 'rejected', 'draft', 'published', 'all']
                : ARTICLE_FILTERS
              ).map((f) => (
                <Chip
                  key={f}
                  clickable
                  label={f === 'all' ? 'All' : articleStatusLabel(f)}
                  color={filter === f ? 'secondary' : 'default'}
                  variant={filter === f ? 'filled' : 'outlined'}
                  onClick={() => {
                    const params = {};
                    if (f !== 'all') params.status = f;
                    if (qParam) params.q = qParam;
                    setSearchParams(params, { replace: true });
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Box>

        {loading && (
          <Box sx={{ p: 2 }}>
            <LoadingState />
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Box sx={{ p: 2 }}>
            <EmptyState
              title={
                qParam
                  ? `No stories match “${qParam}”`
                  : role === 'checker' && filter === 'pending'
                  ? 'Nothing to review yet'
                  : filter === 'pending'
                    ? 'Nothing waiting'
                    : filter === 'draft'
                      ? 'No drafts'
                      : filter === 'approved'
                        ? 'Nothing ready to publish'
                        : filter === 'rejected'
                          ? 'Nothing sent back'
                          : filter === 'published'
                            ? 'Nothing live yet'
                            : 'Nothing here'
              }
              description={
                role === 'checker' && filter === 'pending'
                  ? 'Stories appear here when a writer sends them for review.'
                  : filter === 'draft' && canCreate(role)
                    ? 'Write a new article when you are ready.'
                    : 'Try All, or write a new article if you can.'
              }
              action={
                filter === 'draft' && canCreate(role) ? (
                  <Button
                    component={RouterLink}
                    to="/admin/content/new"
                    variant="contained"
                    color="secondary"
                  >
                    New article
                  </Button>
                ) : filter !== 'all' ? (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSearchInput('');
                      setSearchParams({}, { replace: true });
                    }}
                  >
                    Show all
                  </Button>
                ) : null
              }
            />
          </Box>
        )}

        {!loading &&
          items.map((article, index) => {
            const rejectNoteRaw = (article.auditTrail ?? [])
              .slice()
              .reverse()
              .find((e) => e.action === 'rejected')?.note;
            const rejectNote =
              rejectNoteRaw &&
              rejectNoteRaw !== 'Needs revision' &&
              rejectNoteRaw !== 'Rejected by reviewer'
                ? rejectNoteRaw
                : '';

            return (
              <Box key={article.id}>
                {index > 0 ? <Divider /> : null}
                <Box sx={{ px: { xs: 1.75, sm: 2.25 }, py: { xs: 1.75, sm: 2 } }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Chip
                      size="small"
                      color={article.status === 'rejected' ? 'error' : 'primary'}
                      label={articleStatusLabel(article.status)}
                    />
                    {article.sourceName && (
                      <Typography variant="body2" color="text.secondary">
                        {article.sourceName}
                      </Typography>
                    )}
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 0.5,
                      fontSize: { xs: '1.1rem', sm: '1.2rem' },
                      wordBreak: 'break-word',
                      lineHeight: 1.3,
                    }}
                  >
                    {displayTitle(article.title)}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      wordBreak: 'break-word',
                    }}
                  >
                    {article.summary}
                  </Typography>
                  {article.status === 'rejected' && rejectNote && (
                    <Typography
                      color="error.main"
                      variant="body2"
                      sx={{ mb: 1.5, fontWeight: 600 }}
                    >
                      Reject reason: {rejectNote}
                    </Typography>
                  )}
                  {role === 'admin' ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Created by {article.createdByName || '—'}
                      {article.approvedByName
                        ? ` · reviewed by ${article.approvedByName}`
                        : ''}
                      {article.publishedByName
                        ? ` · published by ${article.publishedByName}`
                        : ''}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      by {article.createdByName || '—'}
                      {article.createdByRole
                        ? ` (${roleLabel(article.createdByRole)})`
                        : ''}
                      {article.submittedByName
                        ? ` · submitted by ${article.submittedByName}`
                        : ''}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {article.createdById === user.id &&
                      (article.status === 'draft' ||
                        article.status === 'rejected') && (
                        <>
                          <Button
                            component={RouterLink}
                            to={`/admin/content/${article.id}/edit`}
                            variant="outlined"
                            size="small"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={busy}
                            onClick={() =>
                              void submit(article.id)
                                .then(() =>
                                  showSuccess('Sent for review'),
                                )
                                .catch((err) =>
                                  showError(
                                    err instanceof Error
                                      ? err.message
                                      : 'Could not send',
                                  ),
                                )
                            }
                          >
                            Send for review
                          </Button>
                          <Button
                            color="error"
                            variant="text"
                            size="small"
                            disabled={busy}
                            onClick={() =>
                              setConfirm({
                                title: 'Delete this draft?',
                                message:
                                  'This cannot be undone.',
                                confirmLabel: 'Delete',
                                danger: true,
                                run: () =>
                                  remove(article.id).then(() =>
                                    showSuccess('Draft deleted'),
                                  ),
                              })
                            }
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    {article.status === 'pending' &&
                      article.createdById === user.id &&
                      !canReviewArticle(role, article, user?.id) && (
                        <Typography variant="body2" color="text.secondary">
                          Waiting for a reviewer. You will see it here again if
                          they send changes.
                        </Typography>
                      )}
                    {article.status === 'published' && (
                      <Button
                        component={RouterLink}
                        to={`/article/${article.id}`}
                        state={{ from: `/admin/content?status=${article.status}` }}
                        variant="outlined"
                        size="small"
                      >
                        View on feed
                      </Button>
                    )}
                    {canReviewArticle(role, article, user?.id) && (
                      <>
                        <Button
                          component={RouterLink}
                          to={`/admin/content/${article.id}/edit`}
                          variant="outlined"
                          size="small"
                        >
                          Review / edit
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={busy}
                          onClick={() =>
                            void approve(article.id)
                              .then(() =>
                                showSuccess('Submitted for publish'),
                              )
                              .catch((err) =>
                                showError(
                                  err instanceof Error
                                    ? err.message
                                    : 'Submit for publish failed',
                                ),
                              )
                          }
                        >
                          Submit for publish
                        </Button>
                        <Button
                          color="error"
                          variant="outlined"
                          size="small"
                          disabled={busy}
                          onClick={() => setRejectTarget(article)}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {canPublishApproved(role, article) && (
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        disabled={busy}
                        onClick={() =>
                          void publish(article.id)
                            .then(() => showSuccess('Published to feed'))
                            .catch((err) =>
                              showError(
                                err instanceof Error
                                  ? err.message
                                  : 'Publish failed',
                              ),
                            )
                        }
                      >
                        Publish to feed
                      </Button>
                    )}
                    {canAdminPublish(role) &&
                      article.status !== 'published' &&
                      article.status !== 'approved' && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={busy}
                        onClick={() =>
                          setConfirm({
                            title: 'Skip review and publish?',
                            message:
                              'This goes live without a reviewer. Prefer Submit for publish when you can.',
                            confirmLabel: 'Publish now',
                            run: () =>
                              publish(article.id).then(() =>
                                showSuccess('Published to feed'),
                              ),
                          })
                        }
                      >
                        Skip review and publish
                      </Button>
                    )}
                    {canAdminPublish(role) && article.status === 'published' && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={busy}
                        onClick={() =>
                          setConfirm({
                            title: 'Remove from the feed?',
                            message:
                              'It becomes a draft. You can edit and publish it again.',
                            confirmLabel: 'Move to drafts',
                            run: () =>
                              unpublish(article.id).then(() =>
                                showSuccess('Moved to drafts'),
                              ),
                          })
                        }
                      >
                        Take off feed
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
            );
          })}
      </ShellCard>
      <RejectReasonDialog
        open={Boolean(rejectTarget)}
        busy={busy}
        onClose={() => setRejectTarget(null)}
        onConfirm={(note) => {
          const article = rejectTarget;
          setRejectTarget(null);
          if (!article) return;
          void reject(article.id, note)
            .then(() => showSuccess('Sent back for changes'))
            .catch((err) =>
              showError(
                err instanceof Error ? err.message : 'Could not send back',
              ),
            );
        }}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={Boolean(confirm?.danger)}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const run = confirm?.run;
          setConfirm(null);
          if (!run) return;
          void Promise.resolve(run()).catch((err) =>
            showError(err instanceof Error ? err.message : 'Could not finish'),
          );
        }}
      />
    </Box>
  );
}
